import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import { serviceRoleClient } from '../../services/serviceRoleClient';

const INDIA_CENTER = [22.5, 79];
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

const COMPLAINT_SELECT =
  'id, customer_name, contact_number, service_type, vehicle_number, pickup_address, pickup_latitude, pickup_longitude, location_link, request_status, complaint_status, source, assigned_mechanic_name, assigned_mechanic_id, problem, requested_at, complaint_registered_at, enterprise_id, enterprise_ref, pincode, delete_status';

const COMPLAINT_SELECT_MIN =
  'id, customer_name, contact_number, service_type, vehicle_number, pickup_address, pickup_latitude, pickup_longitude, location_link, request_status, complaint_status, source, assigned_mechanic_name, problem, requested_at, complaint_registered_at';

const DAY_OPTIONS = [
  { id: 7, label: '7 days' },
  { id: 14, label: '14 days' },
  { id: 30, label: '30 days' },
  { id: 90, label: '90 days' },
];

const SOURCE_FILTERS = [
  { id: 'all', label: 'All sources' },
  { id: 'app', label: 'App' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'b2b', label: 'B2B' },
  { id: 'call', label: 'Call / other' },
];

const STATUS_FILTERS = [
  { id: 'all', label: 'All status' },
  { id: 'open', label: 'Open' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
];

const TYPE_FILTERS = [
  { id: 'all', label: 'All types' },
  { id: 'Jump Start', label: 'Jump Start' },
  { id: 'Towing', label: 'Towing' },
  { id: 'Flat Tyre', label: 'Flat Tyre' },
  { id: 'Fuel Delivery', label: 'Fuel Delivery' },
  { id: 'Key Lockout', label: 'Key Lockout' },
  { id: 'Minor Repair', label: 'Minor Repair' },
];

const CITY_NAMES = [
  'Delhi',
  'New Delhi',
  'Noida',
  'Gurgaon',
  'Gurugram',
  'Ghaziabad',
  'Faridabad',
  'Mumbai',
  'Navi Mumbai',
  'Thane',
  'Pune',
  'Pimpri',
  'Bangalore',
  'Bengaluru',
  'Hyderabad',
  'Chennai',
  'Kolkata',
  'Ahmedabad',
  'Jaipur',
  'Lucknow',
  'Chandigarh',
  'Indore',
  'Bhopal',
  'Nagpur',
  'Patna',
  'Surat',
  'Vadodara',
  'Rajkot',
  'Coimbatore',
  'Kochi',
  'Visakhapatnam',
  'Agra',
  'Kanpur',
  'Nashik',
  'Jodhpur',
  'Udaipur',
  'Dehradun',
  'Ranchi',
  'Bhubaneswar',
  'Mysore',
  'Mysuru',
  'Raipur',
  'Amritsar',
  'Ludhiana',
  'Guwahati',
  'Vijayawada',
  'Madurai',
];

const TYPE_STYLE = {
  'Jump Start': { fill: '#f59e0b', label: 'Jump Start' },
  Towing: { fill: '#7c3aed', label: 'Towing' },
  'Flat Tyre': { fill: '#0ea5e9', label: 'Flat Tyre' },
  'Fuel Delivery': { fill: '#ea580c', label: 'Fuel Delivery' },
  'Key Lockout': { fill: '#e11d48', label: 'Key Lockout' },
  'Minor Repair': { fill: '#059669', label: 'Minor Repair' },
};

const parseLatLng = (lat, lng) => {
  if (lat == null || lng == null || lat === '' || lng === '') return null;
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (a === 0 && b === 0) return null;
  if (a < -90 || a > 90 || b < -180 || b > 180) return null;
  return [a, b];
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const loadLeaflet = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.L) return Promise.resolve(window.L);
  if (window.__roadserveLeafletPromise) return window.__roadserveLeafletPromise;

  window.__roadserveLeafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = LEAFLET_CSS;
      document.head.appendChild(css);
    }
    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => {
      if (window.L) resolve(window.L);
      else reject(new Error('Leaflet failed to load'));
    };
    script.onerror = () => reject(new Error('Leaflet script blocked'));
    document.body.appendChild(script);
  });
  return window.__roadserveLeafletPromise;
};

const normalizeType = (raw) => {
  const tag = String(raw || '').trim();
  if (!tag) return 'Other';
  if (TYPE_STYLE[tag]) return tag;
  const lower = tag.toLowerCase();
  if (lower.includes('fuel') || lower.includes('petrol') || lower.includes('diesel')) return 'Fuel Delivery';
  if (lower.includes('jump') || lower.includes('battery')) return 'Jump Start';
  if (lower.includes('punct') || lower.includes('flat') || lower.includes('tyre') || lower.includes('tire')) {
    return 'Flat Tyre';
  }
  if (lower.includes('tow')) return 'Towing';
  if (lower.includes('lock') || lower.includes('key')) return 'Key Lockout';
  if (lower.includes('minor') || lower.includes('repair') || lower.includes('break')) return 'Minor Repair';
  return tag;
};

const sourceGroup = (source) => {
  const s = String(source || '').toLowerCase();
  if (s.includes('whatsapp')) return 'whatsapp';
  if (s.includes('enterprise') || s.includes('b2b')) return 'b2b';
  if (s.includes('customer_rsa') || s.includes('rsa_app') || s.includes('on_demand') || s.includes('bargain')) {
    return 'app';
  }
  return 'call';
};

const sourceLabel = (source) => {
  const group = sourceGroup(source);
  if (group === 'whatsapp') return 'WhatsApp';
  if (group === 'b2b') return 'B2B';
  if (group === 'app') return 'Customer app';
  const raw = String(source || '').trim();
  return raw || 'Call / other';
};

const statusGroup = (row) => {
  const raw = String(row?.request_status || row?.complaint_status || '')
    .toLowerCase()
    .replace(/_/g, ' ');
  if (raw.includes('complete')) return 'completed';
  if (raw.includes('cancel') || raw.includes('reject') || raw.includes('closed')) return 'cancelled';
  return 'open';
};

const statusLabel = (row) => {
  const group = statusGroup(row);
  if (group === 'completed') return 'Completed';
  if (group === 'cancelled') return 'Cancelled';
  const raw = String(row?.request_status || row?.complaint_status || '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .trim();
  if (!raw) return 'Open';
  if (raw.includes('assign') || raw.includes('on the way') || raw.includes('accepted')) return 'Assigned';
  if (raw.includes('quote') || raw.includes('pending')) return 'Waiting for quote';
  return raw.replace(/\b\w/g, (c) => c.toUpperCase());
};

const detectCity = (row) => {
  const hay = `${row.pickup_address || ''} ${row.location_link || ''}`.toLowerCase();
  const hit = CITY_NAMES.find((name) => hay.includes(name.toLowerCase()));
  if (hit === 'Gurugram') return 'Gurgaon';
  if (hit === 'Bengaluru') return 'Bangalore';
  if (hit === 'Mysuru') return 'Mysore';
  if (hit === 'Pimpri') return 'Pune';
  return hit || '';
};

const whenText = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const typeStyle = (type) => TYPE_STYLE[type] || { fill: '#64748b', label: type || 'Other' };

const fetchComplaints = async (days) => {
  const rows = [];
  let offset = 0;
  const limit = 1000;
  const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
  let select = COMPLAINT_SELECT;
  let triedMin = false;

  while (rows.length < 8000) {
    let q = serviceRoleClient
      .from('service_requests')
      .select(select)
      .not('pickup_latitude', 'is', null)
      .not('pickup_longitude', 'is', null)
      .or(`requested_at.gte.${since},complaint_registered_at.gte.${since}`)
      .or('delete_status.is.null,delete_status.eq.false')
      .order('requested_at', { ascending: false })
      .range(offset, offset + limit - 1);

    const { data, error } = await q;
    if (error) {
      if (!triedMin && select === COMPLAINT_SELECT) {
        select = COMPLAINT_SELECT_MIN;
        triedMin = true;
        continue;
      }
      throw new Error(error.message || 'Could not load complaints');
    }
    const batch = Array.isArray(data) ? data : [];
    if (!batch.length) break;
    rows.push(...batch.filter((row) => !row.delete_status && parseLatLng(row.pickup_latitude, row.pickup_longitude)));
    if (batch.length < limit) break;
    offset += limit;
  }
  return rows;
};

const bindHoverPopup = (layer, html) => {
  layer.bindPopup(html, { maxWidth: 340, maxHeight: 380, autoPanPadding: [24, 24], closeButton: true });
  let closeT = null;
  const cancel = () => {
    if (closeT) {
      clearTimeout(closeT);
      closeT = null;
    }
  };
  layer.on('mouseover', () => {
    cancel();
    layer.openPopup();
    if (typeof layer.bringToFront === 'function') layer.bringToFront();
  });
  layer.on('mouseout', () => {
    cancel();
    closeT = setTimeout(() => layer.closePopup(), 500);
  });
  layer.on('click', () => {
    cancel();
    layer.openPopup();
  });
  layer.on('popupopen', (e) => {
    const el = e.popup.getElement();
    if (!el) return;
    el.addEventListener('mouseenter', cancel);
    el.addEventListener('mouseleave', () => {
      cancel();
      closeT = setTimeout(() => layer.closePopup(), 400);
    });
  });
};

const CustomerComplaintMap = ({ open, onClose }) => {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef(new Map());
  const pinsLayerRef = useRef(null);
  const drawGenRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [days, setDays] = useState(30);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      const type = normalizeType(row.service_type);
      if (sourceFilter !== 'all' && sourceGroup(row.source) !== sourceFilter) return false;
      if (statusFilter !== 'all' && statusGroup(row) !== statusFilter) return false;
      if (typeFilter !== 'all' && type !== typeFilter) return false;
      const city = detectCity(row);
      if (cityFilter !== 'all' && city !== cityFilter) return false;
      if (!q) return true;
      const hay = [
        row.customer_name,
        row.contact_number,
        row.service_type,
        row.pickup_address,
        row.vehicle_number,
        row.assigned_mechanic_name,
        row.source,
        row.problem,
        city,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, sourceFilter, statusFilter, typeFilter, cityFilter, search]);

  const cities = useMemo(() => {
    const counts = new Map();
    rows.forEach((row) => {
      const city = detectCity(row);
      if (!city) return;
      counts.set(city, (counts.get(city) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12);
  }, [rows]);

  const stats = useMemo(() => {
    const byType = {};
    visible.forEach((row) => {
      const type = normalizeType(row.service_type);
      byType[type] = (byType[type] || 0) + 1;
    });
    return {
      shown: visible.length,
      open: visible.filter((r) => statusGroup(r) === 'open').length,
      completed: visible.filter((r) => statusGroup(r) === 'completed').length,
      byType,
    };
  }, [visible]);

  const destroyMap = () => {
    layersRef.current.clear();
    pinsLayerRef.current = null;
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchComplaints(days);
      setRows(data);
    } catch (e) {
      setError(e.message || 'Could not load complaints');
    } finally {
      setLoading(false);
    }
  }, [days]);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    loadData();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      destroyMap();
    };
  }, [open, loadData]);

  useEffect(() => {
    if (!open || loading || !mapEl.current) return undefined;
    const drawId = ++drawGenRef.current;

    const draw = async () => {
      try {
        const L = await loadLeaflet();
        if (drawId !== drawGenRef.current || !mapEl.current) return;
        const createdNow = !mapRef.current;
        if (!mapRef.current) {
          mapRef.current = L.map(mapEl.current, {
            zoomControl: true,
            attributionControl: true,
            minZoom: 4,
            maxZoom: 19,
            maxBounds: [
              [6.5, 67.5],
              [37.5, 97.5],
            ],
            maxBoundsViscosity: 0.8,
          }).setView(INDIA_CENTER, 5);
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; OpenStreetMap',
          }).addTo(mapRef.current);
        }

        const map = mapRef.current;
        if (!map.getPane('complaintPins')) {
          map.createPane('complaintPins');
          map.getPane('complaintPins').style.zIndex = 650;
          map.getPane('complaintPins').style.pointerEvents = 'auto';
        }
        if (pinsLayerRef.current) {
          pinsLayerRef.current.clearLayers();
        } else {
          pinsLayerRef.current = L.layerGroup().addTo(map);
        }
        layersRef.current.clear();

        const groups = new Map();
        visible.forEach((row) => {
          const latlng = parseLatLng(row.pickup_latitude, row.pickup_longitude);
          if (!latlng) return;
          const key = `${latlng[0].toFixed(4)}:${latlng[1].toFixed(4)}`;
          if (!groups.has(key)) groups.set(key, { latlng, rows: [] });
          groups.get(key).rows.push(row);
        });

        groups.forEach((bundle) => {
          const primary = bundle.rows[0];
          const type = normalizeType(primary.service_type);
          const style = typeStyle(type);
          const marker = L.circleMarker(bundle.latlng, {
            pane: 'complaintPins',
            radius: bundle.rows.length > 2 ? 9 : bundle.rows.length > 1 ? 7 : 6,
            color: '#fff',
            weight: 2,
            fillColor: style.fill,
            fillOpacity: 1,
            interactive: true,
            bubblingMouseEvents: false,
          });

          const list = bundle.rows
            .slice(0, 8)
            .map((row) => {
              const rowType = normalizeType(row.service_type);
              const addr = String(row.pickup_address || row.location_link || '').trim() || 'No pickup address';
              const extra = [row.customer_name, row.vehicle_number].filter(Boolean).join(' · ');
              const city = detectCity(row);
              const when = whenText(row.complaint_registered_at || row.requested_at);
              return `<div style="padding:7px 0;border-top:1px solid #e2e8f0">
                <div style="font-weight:800;font-size:12px;color:#0f172a">${escapeHtml(rowType)}</div>
                <div style="font-size:11px;color:#334155;margin-top:2px"><b>From:</b> ${escapeHtml(
                  city ? `${city} · ${addr}` : addr,
                )}</div>
                <div style="font-size:11px;color:#334155"><b>Source:</b> ${escapeHtml(sourceLabel(row.source))}</div>
                <div style="font-size:11px;color:#334155"><b>Status:</b> ${escapeHtml(statusLabel(row))}</div>
                ${when ? `<div style="font-size:11px;color:#334155"><b>When:</b> ${escapeHtml(when)}</div>` : ''}
                ${
                  row.assigned_mechanic_name
                    ? `<div style="font-size:11px;color:#334155"><b>Partner:</b> ${escapeHtml(
                        row.assigned_mechanic_name,
                      )}</div>`
                    : ''
                }
                ${extra ? `<div style="font-size:10px;color:#64748b">${escapeHtml(extra)}</div>` : ''}
                ${
                  row.problem
                    ? `<div style="font-size:10px;color:#64748b;margin-top:2px">${escapeHtml(
                        String(row.problem).slice(0, 140),
                      )}</div>`
                    : ''
                }
                <a href="/complaints/${escapeHtml(row.id)}" target="_blank" rel="noreferrer" style="display:inline-block;margin-top:4px;font-size:11px;color:#e11d48;font-weight:800">Open complaint</a>
              </div>`;
            })
            .join('');

          const html = `<div style="min-width:220px;max-width:320px">
              <div style="font-weight:700;font-size:13px;margin-bottom:2px">Customer complaint</div>
              <div style="font-size:11px;color:#64748b;margin-bottom:4px">${bundle.rows.length} job${
                bundle.rows.length === 1 ? '' : 's'
              } at this pin</div>
              ${list}
              ${
                bundle.rows.length > 8
                  ? `<div style="font-size:11px;color:#64748b;padding-top:6px">+${bundle.rows.length - 8} more</div>`
                  : ''
              }
            </div>`;

          bindHoverPopup(marker, html);
          marker.on('click', () => setSelectedId(primary.id));
          marker.addTo(pinsLayerRef.current);
          bundle.rows.forEach((row) => {
            layersRef.current.set(row.id, { marker, latlng: bundle.latlng });
          });
        });

        if (drawId !== drawGenRef.current) return;
        if (createdNow) {
          map.setView(INDIA_CENTER, 5);
        }
        setTimeout(() => {
          if (drawId !== drawGenRef.current || !mapRef.current) return;
          mapRef.current.invalidateSize();
          if (createdNow) mapRef.current.setView(INDIA_CENTER, 5);
        }, 80);
      } catch (e) {
        if (drawId === drawGenRef.current) setError(e.message || 'Map failed to load');
      }
    };

    draw();
    return () => {
      drawGenRef.current += 1;
    };
  }, [open, loading, visible]);

  const focusComplaint = (row) => {
    setSelectedId(row.id);
    const entry = layersRef.current.get(row.id);
    const map = mapRef.current;
    const latlng = entry?.latlng || parseLatLng(row.pickup_latitude, row.pickup_longitude);
    if (!map || !latlng) return;
    map.flyTo(latlng, 14, { duration: 0.55 });
    if (entry?.marker) entry.marker.openPopup();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-5">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px]"
        aria-label="Close complaint map"
        onClick={onClose}
      />
      <div className="relative z-10 flex h-full max-h-[940px] w-full max-w-[1400px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-rose-600">
              Check complaint
            </p>
            <h3 className="text-lg font-semibold text-slate-900">Where customer complaints came from</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Each pin is a pickup location. Hover to see the service type, address, source and status.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <div className="hidden rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-600 sm:block">
              <span className="font-semibold text-rose-700">{stats.shown}</span> on map ·{' '}
              <span className="font-semibold text-amber-700">{stats.open}</span> open ·{' '}
              <span className="font-semibold text-emerald-700">{stats.completed}</span> completed
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2.5">
          <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {DAY_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setDays(opt.id)}
                className={`rounded-md px-2.5 py-1 text-[11px] font-semibold ${
                  days === opt.id ? 'bg-rose-600 text-white shadow-sm' : 'text-slate-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {SOURCE_FILTERS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setSourceFilter(opt.id)}
                className={`rounded-md px-2.5 py-1 text-[11px] font-semibold ${
                  sourceFilter === opt.id ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {STATUS_FILTERS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setStatusFilter(opt.id)}
                className={`rounded-md px-2.5 py-1 text-[11px] font-semibold ${
                  statusFilter === opt.id ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 px-4 py-2">
          <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Type</span>
          {TYPE_FILTERS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setTypeFilter(opt.id)}
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                typeFilter === opt.id
                  ? 'bg-sky-600 text-white shadow-sm'
                  : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              {opt.label}
              {opt.id !== 'all' && stats.byType[opt.id] ? ` (${stats.byType[opt.id]})` : ''}
            </button>
          ))}
        </div>

        {cities.length ? (
          <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 px-4 py-2">
            <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">City</span>
            <button
              type="button"
              onClick={() => setCityFilter('all')}
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                cityFilter === 'all'
                  ? 'bg-slate-800 text-white shadow-sm'
                  : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              All cities
            </button>
            {cities.map(([name, count]) => (
              <button
                key={name}
                type="button"
                onClick={() => setCityFilter(name)}
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                  cityFilter === name
                    ? 'bg-slate-800 text-white shadow-sm'
                    : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {name} ({count})
              </button>
            ))}
          </div>
        ) : null}

        <div className="flex min-h-0 flex-1">
          <aside className="flex w-[300px] shrink-0 flex-col border-r border-slate-100 bg-slate-50/70">
            <div className="relative border-b border-slate-100 p-2.5">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Find complaint on map…"
                className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-8 pr-2 text-xs text-slate-900 placeholder:text-slate-400 focus:border-rose-300 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-xs text-slate-500">
                  <Loader2 size={14} className="animate-spin" />
                  Loading complaints…
                </div>
              ) : visible.length === 0 ? (
                <p className="px-3 py-8 text-center text-xs text-slate-500">
                  No complaints with GPS in this filter.
                </p>
              ) : (
                visible.slice(0, 250).map((row) => {
                  const type = normalizeType(row.service_type);
                  const style = typeStyle(type);
                  const city = detectCity(row);
                  const addr = String(row.pickup_address || '').trim();
                  return (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => focusComplaint(row)}
                      className={`flex w-full items-start gap-2 border-b border-slate-100 px-3 py-2.5 text-left hover:bg-white ${
                        selectedId === row.id ? 'bg-white ring-1 ring-inset ring-rose-200' : ''
                      }`}
                    >
                      <span
                        className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ background: style.fill }}
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-xs font-semibold text-slate-900">{type}</span>
                        <span className="mt-0.5 block truncate text-[11px] text-slate-500">
                          {city || addr || 'No address'} · {sourceLabel(row.source)} · {statusLabel(row)}
                          {row.assigned_mechanic_name ? ` · ${row.assigned_mechanic_name}` : ''}
                        </span>
                      </span>
                    </button>
                  );
                })
              )}
              {!loading && visible.length > 250 ? (
                <p className="px-3 py-3 text-center text-[11px] text-slate-500">
                  Showing first 250 of {visible.length}. Narrow the filters to see more.
                </p>
              ) : null}
            </div>
          </aside>

          <div className="relative min-w-0 flex-1">
            {error ? (
              <div className="absolute inset-x-4 top-4 z-[500] rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {error}
              </div>
            ) : null}
            <div ref={mapEl} className="h-full w-full bg-slate-100" />
            <div className="pointer-events-none absolute bottom-3 left-3 z-[500] rounded-lg border border-slate-200 bg-white/95 px-2.5 py-2 text-[10px] shadow-sm">
              <p className="mb-1 font-semibold uppercase tracking-wide text-slate-500">Complaint type</p>
              {Object.entries(TYPE_STYLE).map(([key, style]) => (
                <p key={key} className="flex items-center gap-1.5 text-slate-700">
                  <span className="h-2 w-2 rounded-full" style={{ background: style.fill }} /> {style.label}
                </p>
              ))}
              <p className="mt-1 flex items-center gap-1.5 text-slate-700">
                <span className="h-2 w-2 rounded-full bg-slate-400" /> Other
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerComplaintMap;
